import { useEffect, useState } from 'react';
import { MAX_RECORDING_SECONDS, MicRecorder } from '../../audio/recorder';
import { useObservable } from '../../lib/useObservable';
import { useServices } from '../../state/services';
import { Alert, Button, Panel, inputClass } from '../ui';
import type { LibraryActions } from './useAudioLibrary';

const clock = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, '0')}`;

/**
 * Microphone recording. The microphone is requested only when the user presses Record,
 * and released on stop, discard, error or when this panel unmounts. Takes are kept in
 * memory until saved to the library (this browser only) or discarded.
 */
export function RecordingPanel({ library, createRecorder = () => new MicRecorder() }: { library: LibraryActions; createRecorder?: () => MicRecorder }) {
  const { selection } = useServices();
  const [recorder] = useState(createRecorder);
  const state = useObservable(recorder.state);
  const [name, setName] = useState('');
  const [saved, setSaved] = useState<string | null>(null);

  // No background recording: leaving the panel (or the project) releases the microphone.
  useEffect(() => () => recorder.dispose(), [recorder]);

  const takeUrl = state.status === 'recorded' ? (recorder.take?.url ?? null) : null;

  const recording = state.status === 'recording';
  const save = async () => {
    const take = recorder.take;
    if (!take) return;
    const label = name.trim() || `Recording ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    const id = await library.saveRecording(take, label);
    if (id) {
      recorder.discard();
      setName('');
      setSaved(label);
      selection.selectSample(id);
    }
  };

  return (
    <Panel title="Record from microphone">
      <p className="mb-3 text-xs text-zinc-400">
        Recording starts only when you press Record and asks for microphone permission first. Up to {MAX_RECORDING_SECONDS / 60} minutes per take. Takes
        stay in this browser; nothing is uploaded.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {!recording ? (
          <Button variant="primary" onClick={() => void recorder.start().then(() => setSaved(null))} disabled={state.status === 'requesting' || library.busy} data-testid="record-start">
            <span aria-hidden="true" className="text-red-300">●</span> {state.status === 'requesting' ? 'Waiting for permission…' : 'Record'}
          </Button>
        ) : (
          <Button variant="danger" onClick={() => void recorder.stop()} data-testid="record-stop">
            ■ Stop
          </Button>
        )}
        <span
          role="status"
          aria-live="polite"
          className={`text-sm tabular-nums ${recording ? 'text-red-300' : 'text-zinc-400'}`}
          data-testid="record-status"
          data-status={state.status}
        >
          {recording
            ? `Recording ${clock(state.elapsedSec)} · ${(state.sizeBytes / 1024).toFixed(0)} KB`
            : state.status === 'recorded'
              ? `Take ready: ${clock(state.elapsedSec)} · ${(state.sizeBytes / 1024).toFixed(0)} KB (${state.mimeType})`
              : state.status === 'requesting'
                ? 'Asking for microphone access…'
                : 'Microphone off'}
        </span>
      </div>
      {state.notice && <p className="mt-2 text-xs text-amber-300">{state.notice}</p>}
      {state.status === 'error' && (
        <div className="mt-3">
          <Alert title={state.errorCode === 'denied' ? 'Microphone permission denied' : state.errorCode === 'unsupported' ? 'Recording not supported' : 'Recording failed'}>
            {state.error}
          </Alert>
        </div>
      )}
      {state.status === 'recorded' && takeUrl && (
        <div className="mt-3 flex flex-col gap-2 rounded-md border border-zinc-800 bg-zinc-800/40 p-3">
          {/* Preview of the user's own recording: no captions exist. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls src={takeUrl} className="h-9 w-full max-w-md" aria-label="Preview the recording" data-testid="record-preview" />
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="recording-name" className="text-xs text-zinc-300">
                Name
              </label>
              <input id="recording-name" value={name} maxLength={200} placeholder="Recording" onChange={(e) => setName(e.target.value)} className={`${inputClass} w-60`} />
            </div>
            <Button variant="primary" onClick={() => void save()} disabled={library.busy} data-testid="record-save">
              Save to library
            </Button>
            <Button variant="ghost" onClick={() => recorder.discard()} data-testid="record-discard">
              Discard
            </Button>
          </div>
        </div>
      )}
      {saved && <p className="mt-2 text-xs text-emerald-300">Saved “{saved}” to the sample library.</p>}
    </Panel>
  );
}
